import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  List,
  Modal,
  notification,
} from '@duxton-web/ui';
import * as lscache from 'lscache';
import moment from 'moment';
import { useLocation } from 'react-router-dom';

import RolloutDifferenceV2 from '../RolloutDifference/RolloutDifferenceV2';
import { renderInfos, renderTags } from '../RolloutPage/RolloutPage';
import { normalizeRollout } from '../RolloutTab/diffUtils';
import {
  AccessControlWrapper,
  DataTypeIcon,
  ObjectDetailPage,
} from '@/common/components';
import RolloutImpactSummary from '@/common/RolloutImpactSummary';
import { HTTP_STATUS } from '@/constants';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
import { useVariable } from '@/contexts/VariableContext';
import ApproveChangeRequest from '@/features/change-request/approve';
import CanaryRolloutPlanSummary from '@/features/variable/canary-rollout-plan-summary';
import Service from '@/lib/App';
import { RolloutDiff, RolloutTarget } from '@/lib/PlannerDTOs';
import {
  buildAccessTargetFromVariable,
  UserAction,
} from '@/utils/accessControl';
import { getModifiedRolloutTargetsMap } from '@/utils/changelog';
import { appendEnvParam, useAppNavigate } from '@/utils/navigation';
import { getSystemEnvironment } from '@/utils/system-environment';
import { getLoggedUserEmail } from '@/utils/user';

interface RolloutApprovalRequestProps {
  diffId: string;
}

const checklist: string[] = [
  'Check with developers that the new values are within expected range and type.',
  'For production change, make sure that the changes are tested and verified on staging first.',
];

const formatErrorMessage = (e: any) => {
  if (e.response?.data?.arg) {
    const message: string = e.response.data.arg;
    return message.replace('\n', ', ');
  }

  if (e.response != null && e.response.status === HTTP_STATUS.FORBIDDEN) {
    return 'Not enough permissions';
  }

  return 'unknown error';
};

const RolloutApprovalRequest: React.FC<RolloutApprovalRequestProps> = ({
  diffId,
}) => {
  const location = useLocation();
  const { navigateTo } = useAppNavigate();
  const variable = useVariable();
  const [rolloutDiff, setRolloutDiff] = useState<RolloutDiff | null>(null);

  const [showDeclineConfirmModal, setShowDeclineConfirmModal] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [checkListValues, setCheckListValues] = useState<boolean[]>(
    checklist.map(() => false),
  );

  const { permissionUtils } = useUserRolesContext();

  function enrichDiff(diff): void {
    if (!diff?.newRollout?.rollouts?.length) return;

    diff.newRollout.rollouts.forEach((target: RolloutTarget, index: number) => {
      // we are assuming that new target will not have id before they are save in storer
      if (!target?.id) {
        target.id = `newrollout-target-${index}`;
      }
    });
  }

  useEffect(() => {
    const fetchRolloutDiff = async () => {
      const { name } = variable;
      try {
        const diff = await Service.planner!.getRolloutDiff(name, diffId);

        enrichDiff(diff);

        setRolloutDiff(diff);
      } catch (e) {
        const errorMessage = formatErrorMessage(e);
        notification.error({
          description: `Unable to fetch the rollout changes due to : ${errorMessage}`,
          message: 'Fetch of rollout changes',
        });
      }
    };
    fetchRolloutDiff();
  }, []);

  const handleDeclineRolloutChange = async () => {
    const { name } = variable;

    try {
      await Service.planner!.declineRollout(name, diffId);

      notification.success({
        message: 'Approval of rollout',
        description: `Rollout changes were successfully discarded`,
        onClose: () => {
          navigateTo(
            location.pathname.replace(`/approve-request/${diffId}`, ''),
            { replace: true },
          );
        },
        duration: 2,
      });
    } catch (error) {
      const description = `Rollout changes was not successful
            due to: ${formatErrorMessage(error)}`;

      notification.error({
        message: 'Approval of rollout',
        description,
      });
    }
    setShowDeclineConfirmModal(false);
  };

  const handleChecklistChange = (e, ind) => {
    let newV: boolean[] = [...checkListValues];
    newV[ind] = e.target.checked;
    setCheckListValues(newV);
  };

  const renderChecklist = () => {
    return (
      <Alert
        message="Please make sure you have done the following before saving this change."
        type="info"
        closable={false}
        description={
          <List
            dataSource={checklist}
            renderItem={(l, ind) => (
              <List.Item className={'save_button_section__checklist_item'}>
                <Checkbox onChange={(e) => handleChecklistChange(e, ind)}>
                  {l}
                </Checkbox>
              </List.Item>
            )}
          />
        }
      />
    );
  };

  const handleSaveRollout = async () => {
    try {
      const result = await Service.planner!.approveRollout(
        variable.name,
        diffId,
      );
      lscache.set(
        `rollout-cacheexpiry-${variable.name}`,
        { name: variable.name, expireIn: moment() },
        60,
      );
      const isCanary = !!result?.canaryRunId;
      notification.success({
        description: isCanary
          ? `Canary rollout started (run #${result.canaryRunId}). Monitor progress on the variable's Rollout tab.`
          : `Rollout changes were successfully approved and published`,
        message: 'Approval of rollout',
        onClose: () => {
          navigateTo(
            location.pathname.replace(`/approve-request/${diffId}`, ''),
            { replace: true },
          );
        },
        duration: isCanary ? 5 : 2,
      });
    } catch (error) {
      const description = `Rollout changes were not approved
            due to: ${formatErrorMessage(error)}`;

      notification.error({
        description,
        message: 'Approval of rollout',
      });
    }
    setShowApproveConfirmModal(false);
  };

  const renderSecondaryActions = () => {
    if (!rolloutDiff) {
      return null;
    }
    let items = [];
    const isApprovalRequired =
      rolloutDiff.state === 'pending' &&
      moment().diff(moment(rolloutDiff.expireAt)) < 0;
    const userEmail = getLoggedUserEmail();

    const envParam: SystemEnvironment = getSystemEnvironment();
    const isStaging = envParam == SystemEnvironment.STAGING;
    const canApproveChanges = isStaging || userEmail != rolloutDiff?.createdBy;

    if (isApprovalRequired) {
      items.push(
        <>
          <AccessControlWrapper
            action={UserAction.REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE}
            techFamilies={[variable.techFamily]}
            accessTargets={[buildAccessTargetFromVariable(variable)]}
          >
            {(hasAccess) => (
              <Button
                size="small"
                danger
                disabled={!hasAccess}
                onClick={() => {
                  setShowDeclineConfirmModal(true);
                }}
              >
                Reject
              </Button>
            )}
          </AccessControlWrapper>
          <Modal
            open={showDeclineConfirmModal}
            onCancel={() => setShowDeclineConfirmModal(false)}
            cancelButtonProps={{
              size: 'small',
            }}
            title="Decline Changes"
            okText="Decline Rollout"
            okButtonProps={{
              size: 'small',
            }}
            onOk={handleDeclineRolloutChange}
          >
            <Alert
              message="Are you sure you want to discard this rollout changes?"
              type="warning"
            />
          </Modal>
        </>,
      );
    }

    if (isApprovalRequired && canApproveChanges) {
      items.push(
        <>
          <AccessControlWrapper
            action={UserAction.APPROVE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE}
            techFamilies={[variable.techFamily]}
            accessTargets={[buildAccessTargetFromVariable(variable)]}
          >
            {(hasAccess) => (
              <ApproveChangeRequest
                variable={variable}
                diffId={diffId}
                type="primary"
                disabled={!hasAccess}
              />
            )}
          </AccessControlWrapper>
          <Modal
            open={showApproveConfirmModal}
            onCancel={() => {
              setShowApproveConfirmModal(false);
            }}
            cancelButtonProps={{
              size: 'small',
            }}
            title="Confirm Changes"
            okText="Publish Rollout"
            onOk={handleSaveRollout}
            okButtonProps={{
              disabled: checkListValues.includes(false),
              size: 'small',
            }}
          >
            {renderChecklist()}
            <div style={{ marginTop: '10px' }}>
              <Alert
                message="Are you sure you want to publish this rollout changes?"
                type="success"
              />
            </div>
          </Modal>
        </>,
      );
    }
    return items;
  };

  return (
    <ObjectDetailPage
      icon={<DataTypeIcon dataType={variable.type} />}
      breadcrumbs={[
        {
          title: 'Variables',
          path: appendEnvParam('/t/variables'),
        },
        {
          title: variable.name,
          path: appendEnvParam(`/t/variables/${variable.name}/rollout`),
        },
        {
          title: 'Rollout Change Request',
        },
      ]}
      description={variable.desc}
      secondaryActions={renderSecondaryActions()}
      tags={renderTags(variable, permissionUtils)}
      infos={renderInfos(variable)}
    >
      {rolloutDiff && (
        <>
          <RolloutImpactSummary
            rolloutTarget={getModifiedRolloutTargetsMap(
              normalizeRollout(rolloutDiff.newRollout),
              normalizeRollout(rolloutDiff.oldRollout),
            )}
            style={{ margin: 24, marginBottom: 0 }}
          />
          <CanaryRolloutPlanSummary
            variable={variable}
            style={{ margin: 24, marginBottom: 0 }}
          />
        </>
      )}
      <RolloutDifferenceV2 diffId={diffId} rolloutDiff={rolloutDiff} />
    </ObjectDetailPage>
  );
};

export default RolloutApprovalRequest;
